import React, { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Zap,
  Sparkles,
  CheckCircle2,
  Star,
  Briefcase,
  FileText,
  Gauge,
  Target,
  Wrench,
  Film,
  PenLine,
  Users,
} from "lucide-react";

import { getMembers } from "@/lib/api";
import { usePrice } from "@/lib/price";

const ICONS = [Briefcase, FileText, Gauge, Target, Wrench, Film, PenLine, Users];

function FallingIcons() {
  const items = Array.from({ length: 14 }, (_, i) => ({
    Icon: ICONS[i % ICONS.length],
    left: (i * 67) % 100,
    delay: (i * 0.9) % 10,
    duration: 16 + ((i * 3) % 16),
    size: 24 + ((i * 7) % 24),
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {items.map((it, i) => {
        const Icon = it.Icon;
        return (
          <Icon
            key={i}
            size={it.size}
            strokeWidth={2.5}
            className="absolute text-[#0a0a0a]/[0.07]"
            style={{
              left: `${it.left}%`,
              top: "-40px",
              animation: `drift ${it.duration}s linear ${it.delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

export default function Hero() {
  const [members, setMembers] = useState(null);
  const { price } = usePrice();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await getMembers();
        const c = j?.count ?? j?.members;
        if (alive && c != null) setMembers(Number(c).toLocaleString());
      } catch {
        /* silent — fallback copy renders */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="top"
      data-testid="hero"
      className="relative overflow-hidden border-b-4 border-[#0a0a0a] bg-[#FDFBF7] bg-grid"
    >
      <FallingIcons />
      <div className="pointer-events-none absolute -top-20 -right-24 w-[360px] h-[360px] rounded-full bg-[#FFE600] border-4 border-[#0a0a0a] hidden md:block" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-[260px] h-[260px] bg-[#0055FF] border-4 border-[#0a0a0a] rotate-12 hidden md:block" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-end">
          {/* Left: copy */}
          <div className="lg:col-span-7 relative">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-sm shadow-brutal-sm"
            >
              <span className="live-dot" />
              <span data-testid="hero-members" className="font-mono-body">
                {members || "338,947"}
              </span>{" "}
              LIVE ATS JOBS — UPDATED HOURLY
            </div>

            <h1
              data-testid="hero-title"
              aria-label="Hack your career."
              className="font-display mt-6 text-[44px] sm:text-6xl md:text-8xl lg:text-[7.5rem] leading-[0.92] md:leading-[0.88]"
            >
              HACK
              <br />
              YOUR
              <br />
              <span className="relative inline-block">
                <span className="relative z-10 px-2">CAREER.</span>
                <span className="absolute inset-x-0 bottom-2 h-5 md:h-8 bg-[#FFE600] -z-0" />
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-7 max-w-xl font-mono-body text-base md:text-lg text-[#0a0a0a]/80 leading-relaxed"
            >
              The unfair toolkit for ambitious operators.{" "}
              <a href="/jobs" className="bg-[#00FF66] px-1 underline decoration-2 underline-offset-2 text-[#0a0a0a]">
                <strong>338,947 live ATS jobs</strong>
              </a>{" "}
              from 18,000+ company boards (no reposts),{" "}
              <a href="/resume" className="underline decoration-2 underline-offset-2 text-[#0a0a0a]">ATS-bulletproof resumes</a>,
              a 0-100 resume match engine,{" "}
              <a href="/simulator" className="underline decoration-2 underline-offset-2 text-[#0a0a0a]">AI role-play interviews</a>,{" "}
              <a href="/playbooks" className="underline decoration-2 underline-offset-2 text-[#0a0a0a]">100+ grounded playbooks</a>,
              and the content machine that prints offers.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="/login"
                data-testid="hero-cta-free"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-4 border-[#0a0a0a] px-6 md:px-8 py-4 font-accent text-lg md:text-xl shadow-brutal"
              >
                Start Free <ArrowUpRight size={20} strokeWidth={3} />
              </a>
              <a
                href="https://whop.com/checkout/plan_hfsvR7lsOjQds"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-lock"
                className="hover-pop relative inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 md:px-8 py-4 font-accent text-lg md:text-xl shadow-brutal"
              >
                <Zap size={20} strokeWidth={3} />
                GET ALL TOOLS FOR {price}
              </a>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono-body text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} strokeWidth={2.5} className="text-[#0055FF]" />
                One-time payment · lifetime access
              </div>
              <div className="flex items-center gap-2">
                <Sparkles size={18} strokeWidth={2.5} className="text-[#FF3B30]" />
                7-day refund via Whop
              </div>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={16}
                    fill="#FFE600"
                    strokeWidth={1.5}
                    className="text-[#0a0a0a]"
                  />
                ))}
                <span className="font-bold">4.9 · {members || "1,762"} hackers</span>
              </div>
            </div>
          </div>

          {/* Right: hero image */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="relative border-4 border-[#0a0a0a] shadow-brutal-lg overflow-hidden bg-white">
                <img
                  data-testid="hero-image"
                  src="https://images.pexels.com/photos/6193638/pexels-photo-6193638.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Job hacker at work"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                />
                <div className="absolute inset-0 mix-blend-multiply bg-[#0055FF]/10" />
              </div>

              {/* Sticker 1 */}
              <div
                data-testid="hero-sticker-jobs"
                className="absolute -top-3 -left-2 md:-top-5 md:-left-10 bg-[#00FF66] border-4 border-[#0a0a0a] px-3 py-2 md:px-4 md:py-3 shadow-brutal -rotate-[8deg] wiggle"
              >
                <div className="font-display text-xl md:text-3xl leading-none">
                  338K+
                </div>
                <div className="font-accent text-[10px] md:text-xs">LIVE ATS JOBS</div>
              </div>

              {/* Sticker 2 */}
              <div
                data-testid="hero-sticker-deal"
                className="absolute -bottom-4 -right-2 md:-bottom-6 md:-right-8 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-3 py-2 md:px-4 md:py-3 shadow-brutal rotate-[6deg]"
              >
                <div className="font-display text-lg md:text-2xl leading-none">
                  ALL TOOLS
                </div>
                <div className="font-accent text-sm md:text-base">FOR {price}</div>
              </div>

              {/* Floating "interview invite" card */}
              <div className="hidden md:block absolute -left-12 bottom-12 bg-white border-4 border-[#0a0a0a] shadow-brutal p-3 max-w-[220px] rotate-[-4deg]">
                <div className="font-accent text-xs text-[#0a0a0a]/60 mb-1">
                  NEW INTERVIEW INVITE
                </div>
                <div className="font-display text-lg leading-tight">
                  Senior PM @ STRIPE
                </div>
                <div className="flex items-center gap-1 mt-1.5 font-mono-body text-xs">
                  <Sparkles size={12} className="text-[#0055FF]" /> Sourced via
                  Greenhouse ATS
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
